<template>
  <div class="d-flex flex-column row-gap-3">
    <div class="text--medium text--extra-bold">
      {{ $t("project.project-settings.ProjectSettingsMainSettings.title") }}
    </div>
    <AppCard 
      class="border pa-6" 
      data-locator="main-settings-card"
      elevation="0"
    >
      <template v-if="patchRoomPending || currentRoomPending">
        <div class="ProjectSettingsSettings-loader">
          <AppLoader />
        </div>
      </template>
      <v-row>
        <v-col cols="12" md="6">
          <AppTextField v-model.trim="v$.roomName.$model"
                        :label="roomNameLabel"
                        class="ProjectSettingsSettings-roomNameLabel required"
                        maxlength="100"
                        data-locator="room-name-text-input"
                        :counter="100"
                        :error-messages="roomNameErrors"
          />
        </v-col>
        <v-col cols="12" md="6">
          <AppSelect v-model="roomLabel"
                    :items="labelItems"
                    :label="projectLabel"
                    :item-title="`label.${$i18n.locale}`"
                    item-value="id"
                    data-locator="room-label-select-input"
          />
        </v-col>
        <v-col cols="12" md="6">
          <AppDatePicker v-model="dueDate"
                        :label="$t('project.project-settings.ProjectSettingsMainSettings.dueDateLabel')"
                        data-locator="due-date-picker-input"
          >
          </AppDatePicker>
        </v-col>
        <v-col cols="12" md="6">
          <AppTextField v-model.trim="roomCmRef"
                        :label="$t('project.project-settings.ProjectSettingsMainSettings.roomCmRefLabel')"
                        maxlength="100"
                        counter="100"
                        data-locator="customer-file-reference-text-input"
          />
        </v-col>
      </v-row>
      <v-row>
        <ProjectsMetadataSelectGroup
          :room="currentRoom"
          :settingsPage="true"
          :mnemo="mnemo"
          v-model="metadata"
        />
      </v-row>
      <v-row justify="end">
        <v-col cols="auto">
          <ProjectSettingsCloseRoomDialog :mnemo="mnemo"/>
        </v-col>
        <v-col cols="auto">
          <app-button :disabled="saveButtonDisabled"
                      @click="preparePatchRoom"
                      data-locator="save-button"
          >
            {{ $t('project.project-settings.ProjectSettingsMainSettings.save') }}
          </app-button>
        </v-col>
      </v-row>
    </AppCard>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { mapActions, mapGetters, mapState } from 'vuex'

import AppCard from '@/common/AppCard.vue'
import AppDatePicker from '@/common/AppDatePicker.vue'
import AppLoader from '@/common/AppLoader.vue'
import AppSelect from '@/common/AppSelect.vue'
import AppTextField from '@/common/AppTextField.vue'
import { extractOnlyDateFromDateTime } from '@/common/utils/dates'
import { formFieldErrors } from '@/common/utils/form'
import { toast } from '@/common/utils/toast'
import ProjectsMetadataSelectGroup from "@/projects/dialogs/ProjectsMetadataSelectGroup.vue";
import { GET_CURRENT_ROOM, PATCH_ROOM } from '@/store/modules/room/action_types'
import { GET_ROOM_LABELS } from '@/store/modules/rooms/action_types'

import ProjectSettingsCloseRoomDialog from './ProjectSettingsCloseRoomDialog.vue'


export default {
  name: 'ProjectSettingsMainSettings',
  components: {
    ProjectsMetadataSelectGroup,
    AppCard,
    AppDatePicker,
    AppLoader,
    AppSelect,
    AppTextField,
    ProjectSettingsCloseRoomDialog,
  },
  setup: () => ({ v$: useVuelidate() }),
  validations: {
    roomName: { required },
  },
  props: {
    mnemo: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      roomName: '',
      roomCmRef: '',
      dueDate: null,
      roomLabel: null,
      metadata: {},
    }
  },
  computed: {
    ...mapState('room', ['currentRoom', 'currentRoomPending', 'patchRoomPending']),
    ...mapState('rooms', ['roomLabels']),
    ...mapGetters('room', ['isDataRoom']),
    labelItems () {
      const items = [{
        id: null,
        label: { fr: 'Aucun', en: 'None' },
      }]
      if (this.roomLabels) {
        items.push(...this.roomLabels)
      }
      return items
    },
    roomNameErrors () {
      return formFieldErrors(
          this.v$.roomName,
          [
            ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('project.project-settings.ProjectSettingsMainSettings.roomNameLabel') })],
          ],
      )
    },
    saveButtonDisabled () {
      return this.roomName === this.currentRoom.name &&
        extractOnlyDateFromDateTime(this.dueDate) === extractOnlyDateFromDateTime(this.currentRoom.expectedClosingDate) &&
        (this.currentRoom.label ? this.roomLabel === this.currentRoom.label.id : this.roomLabel === null) &&
        (this.currentRoom.hasOwnProperty('cmRef') ? this.roomCmRef === this.currentRoom.cmRef : !this.roomCmRef)
    },
    roomNameLabel () {
      return this.isDataRoom
        ? this.$t('project.project-settings.ProjectSettingsMainSettings.dataRoomNameLabel')
        : this.$t('project.project-settings.ProjectSettingsMainSettings.projectNameLabel')
    },
    projectLabel () {
      return this.isDataRoom
        ? this.$t('project.project-settings.ProjectSettingsMainSettings.dataRoomLabel')
        : this.$t('project.project-settings.ProjectSettingsMainSettings.projectLabel')
    },
  },
  async mounted () {
    try {
      this.GET_ROOM_LABELS(this.mnemo)
    } catch (e) {
      toast.error(this.$t('project.project-settings.ProjectSettingsMainSettings.getRoomsLabelsError'))
    }
    this.roomName = this.currentRoom.name
    this.dueDate = this.currentRoom.expectedClosingDate ? extractOnlyDateFromDateTime(this.currentRoom.expectedClosingDate) : null
    this.roomLabel = this.currentRoom.label ? this.currentRoom.label.id : null
    this.roomCmRef = this.currentRoom.cmRef || ''
    this.metadata = this.currentRoom.metadata || {}
  },
  methods: {
    ...mapActions('room', [GET_CURRENT_ROOM, PATCH_ROOM]),
    ...mapActions('rooms', [GET_ROOM_LABELS]),
    async preparePatchRoom () {
      try {
        const data = {
          name: this.roomName,
          labelId: this.roomLabel,
          expClosingDate: this.dueDate,
          cmRef: this.roomCmRef,
          metadata: this.metadata,
        }
        await this.PATCH_ROOM({
          mnemo: this.mnemo,
          data,
        })
        toast.success(this.$t('project.project-settings.ProjectSettingsMainSettings.patchRoomSuccess'))
        await this.GET_CURRENT_ROOM({ mnemo: this.mnemo, params: null })
      } catch (error) {
        toast.error(this.$t('project.project-settings.ProjectSettingsMainSettings.patchRoomError'))
      }
    },
  },
}
</script>

<style scoped lang="scss">
.ProjectSettingsSettings-loader {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(#fff, .8);
  z-index: 1;
}
:deep(.ProjectSettingsSettings-roomNameLabel .v-text-field__details) {
  margin-bottom: 0;
}
</style>
